<script lang="ts">
  import { onMount } from 'svelte';
  import { theme } from '$stores/theme.svelte';

  interface Props {
    /** WGSL fragment shader source — must define @fragment fn main(...) -> @location(0) vec4f */
    shader: string;
    /** Uniform values passed each frame: [time, intensity, pointerX, pointerY] */
    uniforms?: Float32Array;
    /** CSS class for the wrapper */
    class?: string;
    /** Fallback content when WebGPU unavailable */
    fallback?: import('svelte').Snippet;
  }
  let {
    shader,
    uniforms = new Float32Array([0, 0, 0, 0]),
    class: className = '',
    fallback
  }: Props = $props();

  let canvas: HTMLCanvasElement | undefined = $state();
  let supported = $state(false);
  let animId = 0;

  const FULLSCREEN_VERT = /* wgsl */ `
    @vertex fn vs(@builtin(vertex_index) i: u32) -> @builtin(position) vec4f {
      var pos = array<vec2f, 3>(vec2f(-1,-1), vec2f(3,-1), vec2f(-1,3));
      return vec4f(pos[i], 0, 1);
    }
  `;

  onMount(() => {
    if (!navigator.gpu || !canvas) return;

    let destroyed = false;
    let device: GPUDevice;
    let context: GPUCanvasContext;
    let pipeline: GPURenderPipeline;
    let uniformBuffer: GPUBuffer;
    let bindGroup: GPUBindGroup;
    let format: GPUTextureFormat;

    async function init() {
      try {
        const adapter = await navigator.gpu.requestAdapter();
        if (!adapter || destroyed) return;
        device = await adapter.requestDevice();
        if (destroyed) return;

        context = canvas!.getContext('webgpu')!;
        format = navigator.gpu.getPreferredCanvasFormat();
        context.configure({ device, format, alphaMode: 'premultiplied' });

        const module = device.createShaderModule({
          code: FULLSCREEN_VERT + shader
        });

        uniformBuffer = device.createBuffer({
          size: 32,
          usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
        });

        const bindGroupLayout = device.createBindGroupLayout({
          entries: [
            { binding: 0, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } }
          ]
        });

        bindGroup = device.createBindGroup({
          layout: bindGroupLayout,
          entries: [{ binding: 0, resource: { buffer: uniformBuffer } }]
        });

        pipeline = device.createRenderPipeline({
          layout: device.createPipelineLayout({ bindGroupLayouts: [bindGroupLayout] }),
          vertex: { module, entryPoint: 'vs' },
          fragment: {
            module,
            entryPoint: 'main',
            targets: [
              {
                format,
                blend: {
                  color: { srcFactor: 'src-alpha', dstFactor: 'one-minus-src-alpha' },
                  alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha' }
                }
              }
            ]
          },
          primitive: { topology: 'triangle-list' }
        });

        supported = true;
        frame();
      } catch {
        supported = false;
      }
    }

    function frame() {
      if (destroyed || !device || !context) return;

      const buf = new Float32Array(8);
      buf.set(uniforms);
      buf[4] = canvas!.width;
      buf[5] = canvas!.height;
      buf[6] = theme.current === 'brutalist' ? 1.0 : 0.0;
      buf[7] = theme.resolvedMode === 'light' ? 1.0 : 0.0;
      device.queue.writeBuffer(uniformBuffer, 0, buf);

      const encoder = device.createCommandEncoder();
      const pass = encoder.beginRenderPass({
        colorAttachments: [
          {
            view: context.getCurrentTexture().createView(),
            clearValue: { r: 0, g: 0, b: 0, a: 0 },
            loadOp: 'clear',
            storeOp: 'store'
          }
        ]
      });
      pass.setPipeline(pipeline);
      pass.setBindGroup(0, bindGroup);
      pass.draw(3);
      pass.end();
      device.queue.submit([encoder.finish()]);

      animId = requestAnimationFrame(frame);
    }

    void init();

    return () => {
      destroyed = true;
      cancelAnimationFrame(animId);
      device?.destroy();
    };
  });
</script>

<div class="shader-wrap {className}">
  <canvas
    bind:this={canvas}
    class="shader-canvas"
    class:hidden={!supported}
    width={512}
    height={512}
    aria-hidden="true"
  ></canvas>
  {#if !supported && fallback}
    {@render fallback()}
  {/if}
</div>

<style>
  .shader-wrap {
    position: relative;
    width: 100%;
    height: 100%;
  }
  .shader-canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }
  .shader-canvas.hidden {
    display: none;
  }
</style>
